/**
 * Settings → Webhooks tab.
 *
 *   - Customer/vendor admins manage webhook subscriptions for their
 *     own company (implicit from session).
 *   - Platform admins must pick a target company before creating.
 *
 * Create flow: name + url (+ event type checkboxes) →
 * POST /api/admin/webhooks → modal shows the HMAC secret ONCE.
 */
import React, { useCallback, useEffect, useState } from "react";

import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import { fmtAgo, fmtUtc } from "../../lib/format";
import {
  ALL_WEBHOOK_EVENT_TYPES,
  WEBHOOK_EVENT_LABEL,
  type WebhookCreateResponse,
  type WebhookEventType,
  type WebhookSubscriptionRow,
  type WebhookDeliveryRow,
  createWebhook,
  disableWebhook,
  listWebhooks,
  listWebhookDeliveries,
  testWebhook,
} from "../../services/webhooksApi";
import { fetchCompanies, type Company } from "../../services/adminApi";

export const WebhooksTab: React.FC = () => {
  const { token, role, user } = useAuth();
  const isPlatform = role === "platform";

  const [companies, setCompanies] = useState<Company[]>([]);
  const [targetCid, setTargetCid] = useState<number | null>(
    isPlatform ? null : (user?.company_id ?? null),
  );
  const [subs, setSubs] = useState<WebhookSubscriptionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [newName, setNewName] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [newEvents, setNewEvents] = useState<Set<WebhookEventType>>(
    () => new Set(ALL_WEBHOOK_EVENT_TYPES),
  );
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<WebhookCreateResponse | null>(null);

  const [deliveriesFor, setDeliveriesFor] = useState<number | null>(null);
  const [deliveries, setDeliveries] = useState<WebhookDeliveryRow[]>([]);

  useEffect(() => {
    if (!isPlatform || !token) return;
    fetchCompanies(token)
      .then((cs) =>
        setCompanies(cs.filter((c) => c.company_type === "customer" || c.company_type === "vendor")),
      )
      .catch(() => setCompanies([]));
  }, [isPlatform, token]);

  const load = useCallback(async () => {
    if (!token || targetCid == null) return;
    setLoading(true);
    setError(null);
    try {
      const p = await listWebhooks(token, isPlatform ? targetCid : undefined);
      setSubs(p.subscriptions);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [token, targetCid, isPlatform]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleEvent = (e: WebhookEventType) =>
    setNewEvents((prev) => {
      const next = new Set(prev);
      if (next.has(e)) next.delete(e);
      else next.add(e);
      return next;
    });

  const onCreate = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!token || !newName.trim() || !newUrl.trim() || targetCid == null) return;
    if (newEvents.size === 0) {
      setError("Pick at least one event type.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const allChecked = newEvents.size === ALL_WEBHOOK_EVENT_TYPES.length;
      const resp = await createWebhook(token, {
        name: newName.trim(),
        url: newUrl.trim(),
        ...(isPlatform ? { company_id: targetCid } : {}),
        ...(allChecked ? {} : { event_types: Array.from(newEvents) }),
      });
      setCreated(resp);
      setNewName("");
      setNewUrl("");
      setNewEvents(new Set(ALL_WEBHOOK_EVENT_TYPES));
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onDisable = async (id: number, name: string): Promise<void> => {
    if (!token) return;
    if (!window.confirm(`Disable webhook "${name}"? Future events will not be delivered.`)) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await disableWebhook(token, id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onTest = async (id: number): Promise<void> => {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      const r = await testWebhook(token, id);
      const msg = r.success
        ? `Test delivery succeeded (HTTP ${r.http_status ?? "—"}).`
        : `Test delivery FAILED (HTTP ${r.http_status ?? "—"}): ${r.error_message ?? "unknown"}`;
      window.alert(msg);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onShowDeliveries = async (id: number): Promise<void> => {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      const r = await listWebhookDeliveries(token, id, 25);
      setDeliveries(r.deliveries);
      setDeliveriesFor(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 max-w-5xl">
      <div className="text-xs text-secondary-500">
        Subscribe a URL to receive HMAC-signed POSTs when inventory thresholds change.
        Run <code>python webhook_runner.py --mode service</code> (or schedule oneshot)
        for auto-fire. See <code>docs/api-v1.md</code> for the payload shape and signature recipe.
      </div>

      {isPlatform && (
        <div className="flex items-end gap-2 bg-secondary-50 border border-secondary-200 rounded-md p-3">
          <label className="text-xs text-secondary-600 flex-1">
            <div>Target company</div>
            <select
              value={targetCid ?? ""}
              onChange={(e) => setTargetCid(e.target.value ? Number(e.target.value) : null)}
              className="w-full border border-secondary-300 rounded px-2 py-1 text-sm bg-white"
            >
              <option value="">— Pick a company —</option>
              {companies.map((c) => (
                <option key={c.company_id} value={c.company_id}>
                  {c.company_name} ({c.company_type})
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {error && (
        <div className="text-sm text-error-700 bg-error-50 px-3 py-2 rounded">{error}</div>
      )}

      {targetCid == null ? (
        <div className="text-sm text-secondary-500 bg-white p-6 rounded-md border border-secondary-200">
          Pick a target company above to manage its webhooks.
        </div>
      ) : (
        <>
          {/* Create form */}
          <form
            onSubmit={onCreate}
            className="bg-secondary-50 border border-secondary-200 rounded-md p-3 space-y-2"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <label className="text-xs text-secondary-600">
                <div>Name</div>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Procurement Slack alert"
                  maxLength={128}
                  className="w-full border border-secondary-300 rounded px-2 py-1 text-sm"
                />
              </label>
              <label className="text-xs text-secondary-600">
                <div>Destination URL</div>
                <input
                  type="url"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  placeholder="https://hooks.example.com/mttr"
                  className="w-full border border-secondary-300 rounded px-2 py-1 text-sm"
                />
              </label>
            </div>
            <div>
              <div className="text-xs text-secondary-600 mb-1">Event types</div>
              <div className="flex flex-wrap gap-3">
                {ALL_WEBHOOK_EVENT_TYPES.map((e) => (
                  <label
                    key={e}
                    className="text-xs text-secondary-700 flex items-center gap-1 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={newEvents.has(e)}
                      onChange={() => toggleEvent(e)}
                    />
                    <span>{WEBHOOK_EVENT_LABEL[e]}</span>
                    <code className="text-[10px] text-secondary-500">({e})</code>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end">
              <Button type="submit" size="sm" disabled={busy || !newName.trim() || !newUrl.trim() || newEvents.size === 0}>
                {busy ? "Creating…" : "Create webhook"}
              </Button>
            </div>
          </form>

          {/* List */}
          <div className="bg-white border border-secondary-200 rounded-md overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-secondary-50 text-secondary-700 uppercase">
                <tr>
                  <th className="text-left px-3 py-2">Name / URL</th>
                  <th className="text-left px-3 py-2">Events</th>
                  <th className="text-left px-3 py-2">Health</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100">
                {loading && subs.length === 0 ? (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-secondary-500">loading…</td></tr>
                ) : subs.length === 0 ? (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-secondary-500">No webhooks yet.</td></tr>
                ) : (
                  subs.map((s) => (
                    <tr key={s.subscription_id} className="hover:bg-secondary-50 align-top">
                      <td className="px-3 py-2 font-medium">
                        {s.name}
                        <div className="text-[10px] text-secondary-500 break-all">{s.url}</div>
                      </td>
                      <td className="px-3 py-2">
                        {s.event_types.length === 0 ? (
                          <span className="text-warning-700">all</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {s.event_types.map((e) => (
                              <span
                                key={e}
                                className="text-[10px] bg-secondary-100 border border-secondary-200 text-secondary-700 rounded px-1.5 py-0.5"
                                title={e}
                              >
                                {e.split(".").slice(1).join(".") || e}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-secondary-600">
                        {s.last_success_utc ? (
                          <div><span className="text-success-700">✓</span> {fmtAgo(s.last_success_utc)} ago</div>
                        ) : null}
                        {s.consecutive_failures > 0 ? (
                          <div className="text-error-700">
                            {s.consecutive_failures} fail{s.consecutive_failures === 1 ? "" : "s"}
                            {s.last_failure_utc && ` (${fmtAgo(s.last_failure_utc)} ago)`}
                          </div>
                        ) : null}
                        {!s.last_success_utc && !s.last_failure_utc && (
                          <span className="text-secondary-400">never fired</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {s.disabled_utc ? (
                          <span className="text-error-700" title={`disabled by ${s.disabled_by ?? "?"} at ${fmtUtc(s.disabled_utc)}`}>✕ disabled</span>
                        ) : (
                          <span className="text-success-700">✓ active</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {!s.disabled_utc && (
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="outline" onClick={() => void onTest(s.subscription_id)} disabled={busy}>Test</Button>
                            <Button size="sm" variant="outline" onClick={() => void onShowDeliveries(s.subscription_id)} disabled={busy}>History</Button>
                            <Button size="sm" variant="outline" onClick={() => void onDisable(s.subscription_id, s.name)} disabled={busy}>Disable</Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Delivery-history modal */}
      {deliveriesFor !== null && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 p-4"
        >
          <div className="bg-white rounded-md shadow-xl max-w-3xl w-full p-5 max-h-[80vh] overflow-y-auto">
            <h2 className="text-base font-semibold text-secondary-900">
              Recent deliveries — subscription #{deliveriesFor}
            </h2>
            <table className="min-w-full text-xs mt-3">
              <thead className="bg-secondary-50 text-secondary-700 uppercase">
                <tr>
                  <th className="text-left px-2 py-1">Attempted</th>
                  <th className="text-left px-2 py-1">Event</th>
                  <th className="text-left px-2 py-1">Status</th>
                  <th className="text-left px-2 py-1">HTTP</th>
                  <th className="text-left px-2 py-1">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100">
                {deliveries.length === 0 ? (
                  <tr><td colSpan={5} className="px-2 py-3 text-secondary-500">No deliveries yet.</td></tr>
                ) : (
                  deliveries.map((d) => (
                    <tr key={d.delivery_id}>
                      <td className="px-2 py-1 text-secondary-600">{fmtUtc(d.attempted_utc)}</td>
                      <td className="px-2 py-1"><code className="text-[10px]">{d.event_type}</code></td>
                      <td className="px-2 py-1">
                        {d.success ? <span className="text-success-700">✓</span> : <span className="text-error-700">✕</span>}
                      </td>
                      <td className="px-2 py-1">{d.http_status ?? "—"}</td>
                      <td className="px-2 py-1 text-error-700 text-[10px]">{d.error_message ?? ""}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <div className="mt-3 flex justify-end">
              <Button onClick={() => setDeliveriesFor(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}

      {/* Create-result modal — secret shown ONCE */}
      {created && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 p-4"
        >
          <div className="bg-white rounded-md shadow-xl max-w-lg w-full p-5">
            <h2 className="text-base font-semibold text-secondary-900">
              Webhook created: {created.name}
            </h2>
            <p className="text-sm text-warning-800 bg-warning-50 border border-warning-300 rounded p-2 mt-2">
              {created.warning}
            </p>
            <div className="mt-3">
              <div className="text-xs text-secondary-500 mb-1">Signing secret</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 font-mono text-xs bg-secondary-50 border border-secondary-200 rounded px-2 py-1.5 break-all">
                  {created.secret}
                </code>
                <Button size="sm" variant="outline" onClick={() => { void navigator.clipboard.writeText(created.secret); }}>
                  Copy
                </Button>
              </div>
              <p className="text-[11px] text-secondary-500 mt-2">
                Each POST carries an <code>X-MTTR-Signature: sha256=&lt;hex&gt;</code> header.
                Verify with <code>HMAC-SHA256(secret, request_body_bytes)</code>.
              </p>
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={() => setCreated(null)}>I've saved it</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebhooksTab;
